import React from 'react';
import {
  PlayIcon,
  PauseIcon,
  StopIcon,
  QueueListIcon,
  PhoneXMarkIcon,
} from '@heroicons/react/24/outline';
import { SESSION_STATUS } from '../../../stores/callSessionStore';

/**
 * La barra de sesión: el único sitio donde el gestor decide algo que no sea qué
 * decir.
 *
 * Tres controles y dos números. Nada más, a propósito: cuantas más cosas haya
 * aquí, más veces sale de la conversación.
 */

const LABELS = {
  [SESSION_STATUS.OFFLINE]: { text: 'Sesión cerrada', dot: 'bg-gray-300' },
  [SESSION_STATUS.CONNECTING]: { text: 'Conectando…', dot: 'bg-amber-400 animate-pulse' },
  [SESSION_STATUS.READY]: { text: 'Listo', dot: 'bg-green-500' },
  [SESSION_STATUS.DIALING]: { text: 'Llamando', dot: 'bg-blue-500 animate-pulse' },
  [SESSION_STATUS.CONNECTED]: { text: 'En conversación', dot: 'bg-blue-600' },
  [SESSION_STATUS.WRAP]: { text: 'Cerrando', dot: 'bg-blue-300' },
  [SESSION_STATUS.PAUSED]: { text: 'En pausa', dot: 'bg-amber-500' },
};

/**
 * Aviso de cola a punto de agotarse.
 *
 * Si el gestor se queda sin expedientes marcables, se queda parado sin nada y sin
 * saber por qué. Hay que avisar **antes** de que pase, no cuando ya pasó.
 */
const LOW_QUEUE_THRESHOLD = 10;

const SessionBar = ({
  status,
  queueDepth,
  stats,
  onStart,
  onPause,
  onResume,
  onStop,
  onHangup,
  busy,
}) => {
  const label = LABELS[status] || LABELS[SESSION_STATUS.OFFLINE];
  const offline = status === SESSION_STATUS.OFFLINE;
  const paused = status === SESSION_STATUS.PAUSED;
  const enConversacion = status === SESSION_STATUS.CONNECTED;
  const lowQueue = typeof queueDepth === 'number' && queueDepth <= LOW_QUEUE_THRESHOLD;

  return (
    <div className='flex flex-wrap items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3'>
      <div className='flex items-center gap-6'>
        <span className='flex items-center gap-2 text-sm font-medium text-gray-800'>
          <span className={`h-2.5 w-2.5 rounded-full ${label.dot}`} aria-hidden='true' />
          {label.text}
        </span>

        {!offline && (
          <span
            className={`flex items-center gap-1.5 text-sm ${lowQueue ? 'font-medium text-amber-700' : 'text-gray-500'}`}
            title={lowQueue ? 'Quedan pocos expedientes por llamar' : undefined}
          >
            <QueueListIcon className='h-4 w-4' aria-hidden='true' />
            {queueDepth ?? '—'} en cola
          </span>
        )}
      </div>

      {/*
        Lo que ve al terminar son CONVERSACIONES, no marcaciones. El número de
        llamadas es la métrica de la máquina; la suya es con cuánta gente ha
        hablado y cuánto ha comprometido.
      */}
      {!offline && (
        <div className='flex items-center gap-5 text-sm text-gray-600'>
          <span>
            <span className='font-semibold text-gray-900'>{stats.conversations}</span> conversaciones
          </span>
          <span>
            <span className='font-semibold text-gray-900'>{stats.promises}</span> promesas
          </span>
        </div>
      )}

      <div className='flex items-center gap-2'>
        {offline ? (
          <button
            type='button'
            onClick={onStart}
            disabled={busy}
            className='flex items-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50'
          >
            <PlayIcon className='h-4 w-4' aria-hidden='true' />
            Activar sesión
          </button>
        ) : (
          <>
            {/*
              🔴 Colgar NO existía, y la unica forma de terminar una llamada era
              que colgara el deudor. Uno que se enrolla, que insulta, o que no es
              la persona y no cuelga, dejaba al gestor atrapado — y con la
              llamada abierta NO le entra la siguiente, asi que su jornada
              entera dependia de que colgara alguien sin ningun motivo para
              hacerlo.

              Solo aparece en conversacion: colgar un tono no es cosa suya, de
              los no-contesta se encarga el marcador.
            */}
            {enConversacion && (
              <button
                type='button'
                onClick={onHangup}
                className='flex items-center gap-1.5 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-100'
              >
                <PhoneXMarkIcon className='h-4 w-4' aria-hidden='true' />
                Colgar
              </button>
            )}

            <button
              type='button'
              onClick={paused ? onResume : onPause}
              className='flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              {paused ? <PlayIcon className='h-4 w-4' /> : <PauseIcon className='h-4 w-4' />}
              {paused ? 'Continuar' : 'Dame un segundo'}
            </button>
            <button
              type='button'
              onClick={onStop}
              className='flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50'
            >
              <StopIcon className='h-4 w-4' aria-hidden='true' />
              Cerrar
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export { LOW_QUEUE_THRESHOLD };
export default SessionBar;
